import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { motion } from 'motion/react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { formatarPeriodo } from '@/pages/evento/utils';
import { basePathSite } from '@/pages/assistente-evento/utils';
import type { PerfilEvento } from '@/lib/perfilEvento';
import type { AbasDoEvento } from '../types';
import type { AbaPainel, DadosEventoPainel } from './types';
import ErroAba from './ErroAba';
import AbaInicio from './AbaInicio';
import AbaPessoas from './AbaPessoas';
import AbaFinanceiro from './AbaFinanceiro';
import AbaAjustes from './AbaAjustes';
import AbaPublicoEvento from './AbaPublicoEvento';
import AbaDiaEvento from './dia/AbaDiaEvento';
import TribosDaEquipe from '../components/TribosDaEquipe';
import DivulgarModal from './DivulgarModal';
import MontagemEquipes from '../direcao/components/MontagemEquipes';
import Presencas from '../direcao/components/Presencas';
import ModalPublicar from '../components/ModalPublicar';
import Sheet from '@/components/ui/Sheet';
import { CapaEvento } from '@/components/ui/CapaEvento';

interface EventoOpcaoPainel {
  id: number;
  nome: string;
}

type EstadoAcesso = 'carregando' | 'permitido' | 'negado' | 'nao-encontrado';

// Abas antigas continuam abrindo no lugar certo (?aba=loja, ?aba=ajustes...).
const ABA_LEGADA: Record<string, { aba: AbaPainel; tela?: string }> = {
  ajustes: { aba: 'mais' },
  sorteio: { aba: 'mais', tela: 'sorteio' },
  loja: { aba: 'dia', tela: 'estado' },
  equipe: { aba: 'dia', tela: 'servindo' },
  equipe_dia: { aba: 'dia', tela: 'servindo' },
  portaria: { aba: 'dia', tela: 'portaria' },
};

// Ícone de cada aba (usado nas abas do celular e no menu lateral do computador).
const ICONE_ABA: Record<AbaPainel, string> = {
  inicio: 'ri-home-5-line',
  pessoas: 'ri-group-line',
  equipes: 'ri-layout-grid-line',
  encontros: 'ri-calendar-check-line',
  equipe_acampamento: 'ri-team-line',
  campistas: 'ri-flag-2-line',
  dia: 'ri-store-2-line',
  financeiro: 'ri-money-dollar-circle-line',
  mais: 'ri-more-2-line',
  loja: 'ri-store-2-line',
  equipe: 'ri-team-line',
  sorteio: 'ri-gift-line',
  portaria: 'ri-door-open-line',
  ajustes: 'ri-settings-3-line',
};

// Rótulo e ponto de cor do estado, sobre o herói (vidro branco).
const STATUS_EVENTO: Record<string, { rotulo: string; ponto: string }> = {
  rascunho: { rotulo: 'Rascunho', ponto: 'bg-warning-400' },
  publicado: { rotulo: 'No ar', ponto: 'bg-success-400' },
  encerrado: { rotulo: 'Encerrado', ponto: 'bg-secondary-400' },
};

export default function Painel() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();
  const { eventoId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const [acesso, setAcesso] = useState<EstadoAcesso>('carregando');
  const [erro, setErro] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const [evento, setEvento] = useState<DadosEventoPainel | null>(null);
  const [abas, setAbas] = useState<AbasDoEvento | null>(null);
  const [perfil, setPerfil] = useState<PerfilEvento | null>(null);
  const [usuarioId, setUsuarioId] = useState('');

  const [modalPublicar, setModalPublicar] = useState(false);
  const [modalDivulgar, setModalDivulgar] = useState(false);
  const [seletorAberto, setSeletorAberto] = useState(false);
  const [meusEventos, setMeusEventos] = useState<EventoOpcaoPainel[]>([]);
  const abaAtivaRef = useRef<HTMLButtonElement | null>(null);

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      navigate(`/entrar?voltar=${encodeURIComponent(`/gerenciar/${eventoId}`)}`, {
        replace: true,
      });
      return;
    }
    let ativo = true;
    const eventoIdNum = Number(eventoId);

    async function carregar() {
      setErro(null);
      if (ativo) setUsuarioId(user.id);

      if (Number.isNaN(eventoIdNum)) {
        if (ativo) setAcesso('nao-encontrado');
        return;
      }

      const { data: pode, error: errPode } = await supabase.rpc('pode_editar_evento', {
        p_evento: eventoIdNum,
      });
      if (errPode) {
        if (ativo) {
          setErro(mensagemAmigavel(errPode));
          setAcesso('nao-encontrado');
        }
        return;
      }
      if (!pode) {
        if (ativo) setAcesso('negado');
        return;
      }

      const [resEvento, resAbas, resPerfil] = await Promise.all([
        supabase
          .from('eventos')
          .select(
            'id, dono_id, nome, slug, status, publicado_em, inicio_em, fim_em, cidade, uf, imagem_mobile_url, imagem_desktop_url, local_nome, vende_produtos',
          )
          .eq('id', eventoIdNum)
          .maybeSingle(),
        supabase.from('vw_abas_do_evento').select('*').eq('evento_id', eventoIdNum).maybeSingle(),
        supabase.rpc('perfil_do_evento', { p_evento: eventoIdNum }),
      ]);

      if (resEvento.error) throw resEvento.error;
      if (resAbas.error) throw resAbas.error;
      if (resPerfil.error) throw resPerfil.error;

      if (!resEvento.data) {
        if (ativo) setAcesso('nao-encontrado');
        return;
      }

      if (ativo) {
        setEvento(resEvento.data as DadosEventoPainel);
        setAbas((resAbas.data as AbasDoEvento) ?? null);
        setPerfil((resPerfil.data as PerfilEvento) ?? null);
        setAcesso('permitido');
      }
    }

    carregar().catch((e) => {
      if (ativo) {
        setErro(mensagemAmigavel(e, 'Não foi possível carregar o painel.'));
        setAcesso('nao-encontrado');
      }
    });

    return () => {
      ativo = false;
    };
  }, [navigate, eventoId, reload, pronto, user]);

  const rotulos = perfil?.rotulos;
  const entradaFranca = perfil?.chaves.entrada_franca === true;
  const rotuloPessoas = rotulos?.pessoas || (abas?.vende_ingressos ? 'Compradores' : 'Inscritos');
  const rotuloParticipacoes =
    rotulos?.participacoes || (abas?.vende_ingressos ? 'Ingressos' : 'Inscrições');
  const rotuloPonto = rotulos?.ponto || 'Barraca';
  const rotuloPontos = rotulos?.pontos || 'Barracas';

  const abasVisiveis = useMemo(() => {
    const lista: { valor: AbaPainel; rotulo: string; icone?: string }[] = [];
    const separa = abas?.separa_equipe_campista === true;

    if (abas?.mostrar_visao_geral) lista.push({ valor: 'inicio', rotulo: 'Início' });

    if (separa) {
      // Acampamento: nada de "Inscritos" misturado. Equipe e Campistas, cada um com o seu.
      if (abas?.mostrar_aba_equipe !== false)
        lista.push({ valor: 'equipe_acampamento', rotulo: 'Equipe' });
      if (abas?.mostrar_aba_campistas !== false)
        lista.push({ valor: 'campistas', rotulo: 'Campistas' });
    } else {
      if (!entradaFranca && (abas?.mostrar_inscritos || abas?.vende_ingressos))
        lista.push({ valor: 'pessoas', rotulo: rotuloPessoas });
      if (abas?.mostrar_equipes) lista.push({ valor: 'equipes', rotulo: 'Equipes' });
      if (abas?.mostrar_encontros || abas?.mostrar_presencas)
        lista.push({ valor: 'encontros', rotulo: 'Encontros' });
    }

    // Loja, equipe do dia e portaria agora são um hub só. A loja continua
    // aparecendo em acampamento (separa); portaria e equipe do dia não.
    const temDia = abas?.mostrar_loja === true || (!separa && abas?.vende_ingressos === true);
    if (temDia)
      lista.push({
        valor: 'dia',
        rotulo: 'Dia do evento',
        icone: abas?.mostrar_loja === true ? 'ri-store-2-line' : 'ri-door-open-line',
      });

    if (abas?.mostrar_financeiro) lista.push({ valor: 'financeiro', rotulo: 'Financeiro' });

    lista.push({ valor: 'mais', rotulo: 'Mais' });

    return lista;
  }, [abas, rotuloPessoas, entradaFranca]);

  // Lista discreta para trocar de evento sem sair do painel.
  useEffect(() => {
    let ativo = true;
    (async () => {
      try {
        const { data } = await supabase.rpc('meus_eventos');
        const ids = ((data as unknown[]) || [])
          .map((n) => Number(n))
          .filter((n) => Number.isFinite(n) && n > 0);
        if (ids.length === 0) return;
        const { data: evs } = await supabase.from('eventos').select('id, nome').in('id', ids);
        if (ativo) setMeusEventos((evs as EventoOpcaoPainel[]) || []);
      } catch {
        /* sem seletor: nada muda */
      }
    })();
    return () => {
      ativo = false;
    };
  }, []);

  const abaDaUrl = searchParams.get('aba');
  const separaEquipeCampista = abas?.separa_equipe_campista === true;

  const abaAtiva: AbaPainel = useMemo(() => {
    if (separaEquipeCampista) {
      // Links antigos do acampamento caem na aba nova equivalente.
      if (!abaDaUrl || abaDaUrl === 'inicio') return 'inicio';
      if (
        abaDaUrl === 'pessoas' ||
        abaDaUrl === 'inscritos' ||
        abaDaUrl === 'equipes' ||
        abaDaUrl === 'encontros' ||
        abaDaUrl === 'equipe'
      )
        return 'equipe_acampamento';
      if (abasVisiveis.some((a) => a.valor === abaDaUrl)) return abaDaUrl;
      return abasVisiveis[0]?.valor ?? 'inicio';
    }
    if (abaDaUrl && abasVisiveis.some((a) => a.valor === abaDaUrl)) return abaDaUrl;
    const legada = abaDaUrl ? ABA_LEGADA[abaDaUrl]?.aba : null;
    if (legada && abasVisiveis.some((a) => a.valor === legada)) return legada;
    return abasVisiveis[0]?.valor ?? 'inicio';
  }, [abaDaUrl, abasVisiveis, separaEquipeCampista]);

  // Links antigos salvos (?aba=loja, portaria, sorteio, ajustes, equipe_dia...) são
  // reescritos para a aba e a tela novas, inclusive quando abrem direto pelo endereço.
  useEffect(() => {
    if (!abas) return;
    const aba = searchParams.get('aba');
    if (!aba) return;
    const acampamento = abas.separa_equipe_campista === true;
    let destino: { aba: AbaPainel; tela?: string } | null = null;
    if (aba === 'equipe_dia') destino = { aba: 'dia', tela: 'servindo' };
    else if (aba === 'loja') destino = { aba: 'dia', tela: 'estado' };
    else if (aba === 'portaria') destino = { aba: 'dia', tela: 'portaria' };
    else if (aba === 'sorteio') destino = { aba: 'mais', tela: 'sorteio' };
    else if (aba === 'ajustes') destino = { aba: 'mais' };
    else if (aba === 'equipe')
      destino = acampamento
        ? { aba: 'equipe_acampamento', tela: 'pessoas' }
        : { aba: 'dia', tela: 'servindo' };
    else if (acampamento && (aba === 'equipes' || aba === 'encontros'))
      destino = { aba: 'equipe_acampamento' };
    if (!destino) return;
    if (!abasVisiveis.some((a) => a.valor === destino!.aba)) return;
    const next = new URLSearchParams(searchParams);
    next.set('aba', destino.aba);
    if (destino.tela) next.set('tela', destino.tela);
    else next.delete('tela');
    next.delete('sub');
    setSearchParams(next, { replace: true });
  }, [abas, abasVisiveis, searchParams, setSearchParams]);

  // Sub-aba inicial: aceita ?sub= e os links antigos (?aba=equipes / encontros / pessoas).
  const subDaUrl = searchParams.get('sub');
  const subInicial = useMemo(() => {
    if (abaDaUrl === 'equipes') return 'montagem';
    if (abaDaUrl === 'encontros') return 'formacoes';
    if (abaDaUrl === 'pessoas' || abaDaUrl === 'inscritos' || abaDaUrl === 'equipe')
      return 'pessoas';
    return subDaUrl || 'pessoas';
  }, [abaDaUrl, subDaUrl]);

  useEffect(() => {
    abaAtivaRef.current?.scrollIntoView({ inline: 'nearest', block: 'nearest', behavior: 'smooth' });
  }, [abaAtiva]);

  useEffect(() => {
    if (searchParams.get('divulgar') === '1') {
      setModalDivulgar(true);
      const next = new URLSearchParams(searchParams);
      next.delete('divulgar');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  function aoMudarAba(aba: AbaPainel, extra?: Record<string, string>) {
    const legada = ABA_LEGADA[aba];
    const destino = legada?.aba ?? aba;
    const next = new URLSearchParams(searchParams);
    if (destino === 'inicio') next.delete('aba');
    else next.set('aba', destino);
    // Ao trocar de aba pelo menu, a sub-aba e o filtro antigos não devem ficar presos.
    if (!extra?.sub) next.delete('sub');
    if (!extra?.situacao) next.delete('situacao');
    // A tela aberta dentro de um hub não fica presa ao trocar de aba.
    next.delete('tela');
    if (legada?.tela) next.set('tela', legada.tela);
    Object.keys(extra ?? {}).forEach((k) => {
      if (extra![k]) next.set(k, extra![k]);
      else next.delete(k);
    });
    setSearchParams(next, { replace: true });
  }

  async function aoMudouRecurso() {
    setReload((r) => r + 1);
  }

  if (acesso === 'carregando') {
    return (
      <div className="min-h-screen bg-[#f6f7fb]">
        <div className="mx-auto w-full max-w-[720px] px-4 py-8">
          <div className="esqueleto h-40 rounded-3xl bg-secondary-100" />
          <div className="mt-4 flex gap-2">
            {[0, 1, 2, 3].map((i) => (
              <div key={i} className="esqueleto h-10 w-24 rounded-full bg-secondary-100" />
            ))}
          </div>
          <div className="esqueleto mt-4 h-64 rounded-3xl bg-secondary-100" />
        </div>
      </div>
    );
  }

  if (acesso === 'negado') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f6f7fb] px-4 py-8">
        <div className="w-full max-w-[420px] rounded-3xl bg-white p-8 text-center shadow-[0_6px_18px_rgba(20,25,60,.06)]">
          <h1 className="text-xl font-bold text-foreground-950">
            Você não está na direção deste evento
          </h1>
          <p className="mt-2 text-[15px] text-foreground-600">
            Só quem organiza ou faz parte da direção consegue abrir o painel.
          </p>
          <Link
            to="/meus-eventos"
            className="mt-6 flex w-full cursor-pointer items-center justify-center whitespace-nowrap rounded-2xl bg-primary-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600"
          >
            Meus eventos
          </Link>
        </div>
      </div>
    );
  }

  if (acesso === 'nao-encontrado' || !evento) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#f6f7fb] px-4 py-8">
        <div className="w-full max-w-[420px] rounded-3xl bg-white p-8 text-center shadow-[0_6px_18px_rgba(20,25,60,.06)]">
          <h1 className="text-xl font-bold text-foreground-950">
            {erro ? 'Não foi possível carregar' : 'Evento não encontrado'}
          </h1>
          <p className="mt-2 text-[15px] text-foreground-600">
            {erro ?? 'Este evento não existe ou não está mais disponível.'}
          </p>
          <button
            type="button"
            onClick={() => (erro ? setReload((r) => r + 1) : navigate('/meus-eventos'))}
            className="mt-6 w-full cursor-pointer whitespace-nowrap rounded-2xl bg-primary-500 px-6 py-3.5 text-sm font-semibold text-white shadow-lg shadow-primary-500/30 transition-colors hover:bg-primary-600"
          >
            {erro ? 'Tentar de novo' : 'Meus eventos'}
          </button>
        </div>
      </div>
    );
  }

  const statusInfo = STATUS_EVENTO[evento.status] ?? STATUS_EVENTO.rascunho;
  const ehRascunho = evento.status === 'rascunho';
  const ehPublicado = evento.status === 'publicado';
  const capa = evento.imagem_mobile_url || evento.imagem_desktop_url;
  const temParticipacao = (perfil?.situacao.participacoes ?? 0) > 0;

  const urlEvento = `${window.location.origin}${basePathSite()}/e/${evento.slug}`;
  const ehInicio = abaAtiva === 'inicio';

  return (
    <div className="min-h-screen bg-[#f6f7fb]">
      {/* Computador: menu lateral fixo, no lugar da fileira de abas */}
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-[236px] flex-col border-r border-background-200 bg-white lg:flex">
        <div className="px-4 pb-3 pt-4">
          <Link
            to="/meus-eventos"
            className="flex min-h-[36px] cursor-pointer items-center gap-1 whitespace-nowrap text-[13px] font-semibold text-foreground-600 transition-colors hover:text-foreground-950"
          >
            <span className="flex h-4 w-4 items-center justify-center">
              <i className="ri-arrow-left-line" aria-hidden />
            </span>
            Meus eventos
          </Link>
          <p className="mt-2 truncate text-[15px] font-extrabold text-foreground-950">
            {evento.nome}
          </p>
          <span className="mt-1 inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-background-100 px-2.5 py-0.5 text-[11.5px] font-semibold text-foreground-700">
            <span className={`h-1.5 w-1.5 rounded-full ${statusInfo.ponto}`} />
            {statusInfo.rotulo}
          </span>
        </div>
        <nav className="flex-1 overflow-y-auto px-2 pb-4">
          {abasVisiveis.map((a) => {
            const ativo = abaAtiva === a.valor;
            return (
              <button
                key={a.valor}
                type="button"
                onClick={() => aoMudarAba(a.valor)}
                className={`relative mb-0.5 flex min-h-[44px] w-full cursor-pointer items-center gap-2.5 rounded-xl px-3 text-left text-[14px] font-semibold transition-colors ${
                  ativo
                    ? 'text-white'
                    : 'text-foreground-700 hover:bg-background-100'
                }`}
              >
                {ativo && (
                  <motion.span
                    layoutId="aba-menu-painel"
                    className="absolute inset-0 rounded-xl bg-primary-500"
                    transition={{ duration: 0.2, ease: 'easeOut' }}
                  />
                )}
                <span className="relative flex h-5 w-5 shrink-0 items-center justify-center">
                  <i className={`${a.icone ?? ICONE_ABA[a.valor]} text-[18px]`} aria-hidden />
                </span>
                <span className="relative min-w-0 flex-1 truncate">{a.rotulo}</span>
              </button>
            );
          })}
        </nav>
      </aside>

      <div className="lg:pl-[236px]">
      {/* Herói = a capa do evento desfocada, como na compra */}
      <header className={`relative overflow-hidden ${ehInicio ? 'min-h-[210px]' : 'min-h-[150px]'}`}>
        {capa ? (
          <img
            src={capa}
            alt=""
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover object-center"
            style={{ filter: 'blur(20px)', transform: 'scale(1.1)' }}
            loading="lazy"
            decoding="async"
          />
        ) : (
          <div className="absolute inset-0 bg-gradient-to-br from-primary-600 via-primary-500 to-primary-400" />
        )}
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/35 to-black/55" />

        <div className="relative z-10 mx-auto w-full max-w-[720px] px-4 pb-3 pt-4 lg:max-w-[1080px] lg:px-6">
          {/* linha 1: voltar + chip */}
          <div className="flex items-center justify-between gap-3">
            <Link
              to="/meus-eventos"
              className="flex min-h-[44px] cursor-pointer items-center gap-1 whitespace-nowrap text-[15px] font-medium text-white/90 transition-colors hover:text-white"
            >
              <span className="flex h-6 w-6 items-center justify-center">
                <i className="ri-arrow-left-line" />
              </span>
              Meus eventos
            </Link>
            <div className="flex items-center gap-2">
              <Link
                to={`/gerenciar/${evento.id}/assistente`}
                className="flex h-9 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-full bg-white/20 px-3 text-[13px] font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/30"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-sparkling-2-line" aria-hidden />
                </span>
                Seu agente
              </Link>
              <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-white/20 px-3 py-1 text-[13px] font-semibold text-white backdrop-blur-sm">
                <span className={`h-1.5 w-1.5 rounded-full ${statusInfo.ponto}`} />
                {statusInfo.rotulo}
              </span>
            </div>
          </div>

          {/* nome (1 linha) + seletor discreto */}
          <div className="mt-2 flex items-center gap-2.5">
            {capa && (
              <span className="block h-[54px] w-[44px] shrink-0 overflow-hidden rounded-[8px] border border-white/25">
                <CapaEvento
                  mobile={evento.imagem_mobile_url}
                  desktop={evento.imagem_desktop_url}
                  alt={evento.nome}
                  preencher
                  className="!rounded-[8px]"
                />
              </span>
            )}
            <h1 className="min-w-0 truncate text-[22px] font-extrabold leading-tight text-white">
              {evento.nome}
            </h1>
            {meusEventos.length > 1 && (
              <button
                type="button"
                onClick={() => setSeletorAberto(true)}
                aria-label="Trocar de evento"
                className="aperta flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full bg-white/20 text-white backdrop-blur-sm"
              >
                <i className="ri-arrow-down-s-line text-xl" aria-hidden />
              </button>
            )}
          </div>
          <p className="mt-1 text-[13px] text-white/85">
            {formatarPeriodo(evento.inicio_em, evento.fim_em)}
            {evento.cidade ? ` · ${evento.cidade}${evento.uf ? `, ${evento.uf}` : ''}` : ''}
          </p>

          {/* ações em pílulas de vidro */}
          <div className="mt-3 flex items-stretch gap-2">
            <button
              type="button"
              onClick={() => window.open(urlEvento, '_blank', 'noopener,noreferrer')}
              className="flex h-11 min-w-0 flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl bg-white/20 px-3 text-[13px] font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/30"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-eye-line" />
              </span>
              Ver
            </button>
            {ehPublicado && (
              <button
                type="button"
                onClick={() => setModalDivulgar(true)}
                className="flex h-11 min-w-0 flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl bg-white/20 px-3 text-[13px] font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/30"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-megaphone-line" />
                </span>
                Divulgar
              </button>
            )}
            {!ehPublicado && evento.status !== 'encerrado' && (
              <button
                type="button"
                onClick={() => setModalPublicar(true)}
                className="flex h-11 min-w-0 flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl bg-white px-3 text-[13px] font-bold text-primary-700 shadow-lg transition-colors hover:bg-primary-50"
              >
                <span className="flex h-4 w-4 items-center justify-center">
                  <i className="ri-rocket-2-line" />
                </span>
                Publicar
              </button>
            )}
            <Link
              to={`/evento/${evento.id}/editar`}
              className="flex h-11 min-w-0 flex-1 cursor-pointer items-center justify-center gap-1.5 whitespace-nowrap rounded-2xl bg-white/20 px-3 text-[13px] font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/30"
            >
              <span className="flex h-4 w-4 items-center justify-center">
                <i className="ri-edit-line" />
              </span>
              Editar
            </Link>
          </div>
        </div>
      </header>

      {/* Abas em pílulas, grudadas no topo (só no celular) */}
      <nav className="sticky top-0 z-30 bg-[#f6f7fb] lg:hidden">
        <div className="relative mx-auto w-full max-w-[720px] px-4 py-2">
          <div className="overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
            <div className="flex gap-2">
              {abasVisiveis.map((a) => (
                <button
                  key={a.valor}
                  ref={abaAtiva === a.valor ? abaAtivaRef : null}
                  type="button"
                  onClick={() => aoMudarAba(a.valor)}
                  className={`relative flex h-10 shrink-0 cursor-pointer items-center whitespace-nowrap rounded-full border px-4 text-[14px] font-semibold transition-colors ${
                    abaAtiva === a.valor
                      ? 'border-primary-500 text-white'
                      : 'border-primary-100 bg-white text-foreground-700 hover:bg-primary-50'
                  }`}
                >
                  {abaAtiva === a.valor && (
                    <motion.span
                      layoutId="aba-pilula-painel"
                      className="absolute inset-0 rounded-full bg-primary-500 shadow-lg shadow-primary-500/30"
                      transition={{ duration: 0.2, ease: 'easeOut' }}
                    />
                  )}
                  <span className="relative mr-1.5 flex h-4 w-4 items-center justify-center">
                    <i className={a.icone ?? ICONE_ABA[a.valor]} />
                  </span>
                  <span className="relative">{a.rotulo}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </nav>

      <main className="mx-auto w-full max-w-[720px] px-4 py-4 pb-16 lg:max-w-[1080px] lg:px-6">
        {abaAtiva === 'inicio' && (
          <ErroAba>
            <AbaInicio
              eventoId={evento.id}
              slug={evento.slug}
              vendeIngressos={abas?.vende_ingressos === true}
              rotuloPessoas={rotuloPessoas}
              mostrarEquipes={abas?.mostrar_equipes === true}
              mostrarLoja={abas?.mostrar_loja === true}
              separaEquipeCampista={separaEquipeCampista}
              entradaFranca={entradaFranca}
              status={evento.status}
              aoIrPara={aoMudarAba}
              aoPublicar={() => setModalPublicar(true)}
            />
          </ErroAba>
        )}
        {abaAtiva === 'equipe_acampamento' && (
          <ErroAba>
            <AbaPublicoEvento
              eventoId={evento.id}
              eventoNome={evento.nome}
              eventoCapa={capa}
              eventoLocal={evento.local_nome}
              usuarioId={usuarioId}
              eventoInicio={evento.inicio_em ?? ''}
              mostrarFicha={abas?.mostrar_ficha === true}
              papel="equipe"
              subInicial={subInicial}
            />
          </ErroAba>
        )}
        {abaAtiva === 'campistas' && (
          <ErroAba>
            <AbaPublicoEvento
              eventoId={evento.id}
              eventoNome={evento.nome}
              eventoCapa={capa}
              eventoLocal={evento.local_nome}
              usuarioId={usuarioId}
              eventoInicio={evento.inicio_em ?? ''}
              mostrarFicha={abas?.mostrar_ficha === true}
              papel="campista"
              subInicial={subInicial}
            />
          </ErroAba>
        )}
        {abaAtiva === 'pessoas' && (
          <ErroAba>
            {abas?.mostrar_equipes !== true && <TribosDaEquipe eventoId={evento.id} />}
            <AbaPessoas
              eventoId={evento.id}
              eventoNome={evento.nome}
              eventoCapa={capa}
              eventoLocal={evento.local_nome}
              vendeIngressos={abas?.vende_ingressos === true}
              mostrarFicha={abas?.mostrar_ficha === true}
              rotuloParticipacoes={rotuloParticipacoes}
              usuarioId={usuarioId}
              eventoInicio={evento.inicio_em ?? ''}
            />
          </ErroAba>
        )}
        {abaAtiva === 'equipes' && (
          <ErroAba>
            <MontagemEquipes eventoId={evento.id} />
          </ErroAba>
        )}
        {abaAtiva === 'encontros' && (
          <ErroAba>
            <Presencas eventoId={evento.id} />
          </ErroAba>
        )}
        {abaAtiva === 'dia' && (
          <ErroAba>
            <AbaDiaEvento
              eventoId={evento.id}
              eventoNome={evento.nome}
              mostrarLoja={abas?.mostrar_loja === true}
              vendeIngressos={abas?.vende_ingressos === true}
              entradaFranca={entradaFranca}
              rotuloPonto={rotuloPonto}
              rotuloPontos={rotuloPontos}
            />
          </ErroAba>
        )}
        {abaAtiva === 'financeiro' && (
          <ErroAba>
            <AbaFinanceiro
              eventoId={evento.id}
              mostrarLoja={abas?.mostrar_loja === true}
              entradaFranca={entradaFranca}
              separaEquipeCampista={separaEquipeCampista}
              aoIrPara={aoMudarAba}
            />
          </ErroAba>
        )}
        {abaAtiva === 'mais' && (
          <ErroAba>
            <AbaAjustes
              eventoId={evento.id}
              perfil={perfil}
              mostrarEquipes={abas?.mostrar_equipes === true}
              mostrarRifa={abas?.mostrar_rifa === true}
              aoMudouRecurso={aoMudouRecurso}
            />
          </ErroAba>
        )}
      </main>

      {modalPublicar && (
        <ModalPublicar
          evento={{
            id: evento.id,
            nome: evento.nome,
            slug: evento.slug,
            inicio_em: evento.inicio_em,
            local_nome: evento.local_nome,
            cidade: evento.cidade,
            capa: capa,
            vende_produtos: evento.vende_produtos,
          }}
          temParticipacao={temParticipacao}
          aoFechar={() => setModalPublicar(false)}
          aoPublicado={() => {
            setModalPublicar(false);
            setReload((r) => r + 1);
          }}
        />
      )}

      {modalDivulgar && (
        <DivulgarModal
          eventoId={evento.id}
          eventoNome={evento.nome}
          slug={evento.slug}
          mostrarLoja={abas?.mostrar_loja === true}
          vendeIngressos={abas?.vende_ingressos === true}
          aoFechar={() => setModalDivulgar(false)}
        />
      )}

      <Sheet
        aberto={seletorAberto}
        titulo="Trocar de evento"
        aoFechar={() => setSeletorAberto(false)}
      >
        <div className="space-y-2">
          {meusEventos.map((ev) => {
            const atual = ev.id === evento.id;
            return (
              <button
                key={ev.id}
                type="button"
                onClick={() => {
                  setSeletorAberto(false);
                  if (!atual) navigate(`/gerenciar/${ev.id}`);
                }}
                className={`aperta flex w-full cursor-pointer items-center gap-3 rounded-[18px] px-4 py-3.5 text-left transition-colors ${
                  atual ? 'bg-primary-50' : 'bg-background-100'
                }`}
              >
                <span className="min-w-0 flex-1 truncate text-[15px] font-semibold text-foreground-950">
                  {ev.nome}
                </span>
                {atual ? (
                  <i className="ri-check-line text-primary-600" aria-hidden />
                ) : (
                  <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden />
                )}
              </button>
            );
          })}
        </div>
      </Sheet>
      </div>
    </div>
  );
}